import { useEffect, useRef, type PointerEvent } from "react";
import smartMockupsImage from "assets/images/linksroom-smart-mockups.webp";
import { AppCtaButtons, Image } from "components";
import { config } from "constant";
import { useToggle } from "hooks";
import { twMerge } from "tailwind-merge";

type Props = {
  onShowLess: VoidFunction;
};

type DetailItem = {
  title: string;
  copy: string;
};

const reasons: DetailItem[] = [
  {
    title: "All Your Links, Files & Content in One Place",
    copy: "Organize personal, professional, business, and social links alongside files, posts, articles, products, banners, and more.",
  },
  {
    title: "Personal or Business Profile",
    copy: "Choose the profile type that fits you best and set up your LinksRoom experience around the way you actually use it.",
  },
  {
    title: "Smart Access Control",
    copy: "Decide who can see what: public visitors, friends, selected contacts, approved people, or private audiences.",
  },
  {
    title: "Rooms for Shared Content",
    copy: "Create rooms to organize links, files, posts, articles, products, and people around a topic, team, event, or community.",
  },
  {
    title: "Profile Welcome Message",
    copy: "Add a custom welcome message to introduce yourself, your work, your brand, or your room from the first visit.",
  },
  {
    title: "Bookmark Import",
    copy: "Import a browser bookmark file and add multiple links to your LinksRoom faster, without entering every link manually.",
  },
  {
    title: "Reshare & Discover",
    copy: "Reshare your own content or content from others, explore shared content, and grow through community engagement.",
  },
  {
    title: "Chat, Replies & Reactions",
    copy: "Connect with people, send requests, start conversations, reply to messages, and react naturally inside chats.",
  },
  {
    title: "Real-Time Analytics",
    copy: "Track profile views, link clicks, content performance, audience behavior, and growth with clear insights.",
  },
  {
    title: "Digital Business Card + QR Code",
    copy: "Share your profile instantly from your home screen, QR code, or public LinksRoom link.",
  },
  {
    title: "Local Communication",
    copy: "Discover and connect with people near you — perfect for events, meetups, campuses, coworking spaces, and professional networking.",
  },
];

const newFeatures = [
  "Promote your links, posts, products, rooms, and profile",
  "Access advanced analytics and insights",
  "Customize your profile with beautiful themes",
  "Create posts and articles",
  "Add banners to highlight key content",
  "Build your own mini store inside your profile",
  "Manage orders and showcase products",
  "Use smarter notifications and improved messaging",
  "Share public rooms and invite people more easily",
];

const previewReasonCount = 4;

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const easeInOutCubic = (value: number) =>
  value < 0.5 ? 4 * value ** 3 : 1 - ((-2 * value + 2) ** 3) / 2;

export default function CheckMore({ onShowLess }: Props) {
  const [showMore, toggleShowMore] = useToggle(false);
  const checkMoreRef = useRef<HTMLDivElement>(null);
  const mockupsRef = useRef<HTMLDivElement>(null);
  const handleToggle = () => {
    setTimeout(() => {
      showMore && onShowLess();
    }, 350);
    toggleShowMore();
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== "mouse") {
      return;
    }

    const bounds = event.currentTarget.getBoundingClientRect();

    event.currentTarget.style.setProperty(
      "--cursor-x",
      `${(event.clientX - bounds.left).toFixed(1)}px`,
    );
    event.currentTarget.style.setProperty(
      "--cursor-y",
      `${(event.clientY - bounds.top).toFixed(1)}px`,
    );
    event.currentTarget.classList.add("is-cursor-active");
  };

  const handlePointerLeave = (event: PointerEvent<HTMLDivElement>) => {
    event.currentTarget.classList.remove("is-cursor-active");
  };

  useEffect(() => {
    const checkMore = checkMoreRef.current;
    const mockups = mockupsRef.current;

    if (!checkMore || !mockups) {
      return undefined;
    }

    let animationFrame = 0;

    const syncScrollProgress = () => {
      const bounds = checkMore.getBoundingClientRect();
      const start = window.innerHeight * 0.98 - 100;
      const end = window.innerHeight * 0.08 - 100;
      const rawProgress = clamp((start - bounds.top) / (start - end), 0, 1);
      const progress = easeInOutCubic(rawProgress);

      mockups.style.setProperty("--mockup-progress", progress.toFixed(4));
      mockups.style.setProperty(
        "--mockup-opacity",
        (0.025 + progress * 0.975).toFixed(4),
      );
      mockups.style.setProperty(
        "--mockup-scale",
        (0.5 + progress * 0.5).toFixed(4),
      );
      mockups.style.setProperty(
        "--mockup-rotate",
        `${((1 - progress) * -8).toFixed(4)}deg`,
      );
    };

    const requestSync = () => {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(syncScrollProgress);
    };

    syncScrollProgress();
    window.addEventListener("scroll", requestSync, { passive: true });
    window.addEventListener("resize", requestSync);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener("scroll", requestSync);
      window.removeEventListener("resize", requestSync);
    };
  }, []);

  return (
    <div
      ref={checkMoreRef}
      onPointerMove={handlePointerMove}
      onPointerLeave={handlePointerLeave}
      style={{
        background: "linear-gradient(106.54deg, #353740 8.92%, #515669 91.31%)",
      }}
      className="smart-checkmore w-full rounded-[2.5rem] mt-5"
    >
      <header className="smart-checkmore-intro">
        <h3>
          <span>LinksRoom</span>
          <strong>The Smart Way to Organize, Share & Grow Your Links</strong>
        </h3>
        <div className="smart-checkmore-lede">
          <p>LinksRoom — Organize, Share & Grow Your Digital Identity</p>
          <p>Every day, we share more than just links.</p>
          <p>
            We share social profiles, phone numbers, portfolios, files,
            products, posts, articles, payment links, business pages, private
            information, and professional updates.
          </p>
          <p>
            But managing all of it across different apps, deciding who can see
            what, and keeping everything up to date should not be this
            complicated.
          </p>
        </div>
        <h4>Meet LinksRoom</h4>
        <div className="smart-checkmore-meet-copy">
          <p>
            LinksRoom is a smart, privacy-first platform where your links,
            files, content, rooms, messages, and business tools come together in
            one beautiful profile.
          </p>
          <p>It is more than a link-in-bio tool.</p>
          <p>It is a social network for your digital identity.</p>
        </div>
      </header>

      <div ref={mockupsRef} className="smart-checkmore-mockups-stage">
        <div className="smart-checkmore-mockups-float">
          <Image
            src={smartMockupsImage}
            alt={config.title}
            className="smart-checkmore-mockups"
          />
        </div>
      </div>

      <div className="smart-checkmore-details">
        <section>
          <h5>🚀 Why LinksRoom?</h5>
          <ul>
            {reasons.map((item, index) => (
              <li
                key={item.title}
                className={twMerge(
                  index >= previewReasonCount &&
                    (showMore ? "" : "hidden xl:list-item"),
                )}
              >
                <strong>{item.title}</strong>
                <span>{item.copy}</span>
              </li>
            ))}
          </ul>
          <button
            type="button"
            className="smart-checkmore-toggle xl:hidden"
            onClick={handleToggle}
          >
            {showMore ? "Show less" : "Show more"}
          </button>
        </section>

        <section className={twMerge(showMore ? "grid" : "hidden xl:grid")}>
          <h5>✨ New in LinksRoom 3.0 (New Features)</h5>
          <p>
            Built for creators, freelancers, professionals, and businesses who
            want more control and more growth.
          </p>
          <ul>
            {newFeatures.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <p className="smart-checkmore-ready">
            Start Free. Grow When You’re Ready.
          </p>
          <p>
            Whether you are showcasing your work, organizing your business,
            building a community, selling products, sharing private links, or
            simplifying your digital presence, LinksRoom gives you clarity,
            control, and growth in one place.
          </p>
          <p>
            💬 We’d love your feedback. What do you love? What can we improve?
          </p>
          <p>Your links. Your content. Your privacy. Your room.</p>
          <p>We would love your feedback.</p>
          <p>
            Tell us what you love, what we can improve, or what you want to see
            next.
          </p>
        </section>

        <section
          className={twMerge(
            "smart-checkmore-signoff",
            showMore ? "grid" : "hidden xl:grid",
          )}
        >
          <p>📩 gataastudio@gmail.com</p>
          <p>
            With love,
            <br />
            Gataastudio ❤️
          </p>
        </section>
      </div>

      <AppCtaButtons className="smart-checkmore-stores" />
    </div>
  );
}
